import { type ApolloClient } from "@apollo/client";
import { type ProfileRestingModelEntry } from "@package/ui-gql/index-data-access";
import { type SliceWallState } from "../redux/slice-wall";
import { EventEmitter } from "../utils/event-emitter";
import {
  getLocationActiveProfile,
  getMedia,
  getMediaType,
  getProfile,
} from "../utils/wall-utils-gql";

const DEFAULT_IMAGE_DURATION_MS = 30 * 1000;
const DEFAULT_RESPONSE: SliceWallState["resting"] = {
  active: false,
  audio: false,
  interrupt: true,
  transparent: true,
  type: "image",
};

type RestingEngineEvents = {
  "resting-updated": (update: SliceWallState["resting"]) => void;
};

export class WallEngineResting extends EventEmitter<RestingEngineEvents> {
  private timeoutStart?: ReturnType<typeof setTimeout>;
  private timeoutPlayingMedia?: ReturnType<typeof setTimeout>;

  private activating: boolean = false;
  private starting: boolean = false;
  private mediaIndex: number = -1;

  constructor(
    private readonly client: ApolloClient<object>,
    private readonly location?: string,
    private readonly profile?: string,
  ) {
    super();

    if (!this.location && !this.profile) {
      throw new Error("A location or profile is required");
    }
  }

  public destroy(): void {
    super.destroy();
    this.cancelTimeoutStart();
    this.cancelTimeoutPlayingMedia();
    console.log("[wall-engine-resting] Destroyed");
  }

  public async activate(): Promise<void> {
    if (this.activating) {
      return;
    }
    this.activating = true;
    console.log("[wall-engine-resting] Activating...");

    this.cancelTimeoutPlayingMedia();
    this.cancelTimeoutStart();

    this.emit("resting-updated", DEFAULT_RESPONSE);

    this.timeoutStart = setTimeout(
      () => this.start(),
      await this.getStaleTimeoutDelay(),
    );

    console.log("[wall-engine-resting] Activated");
    this.activating = false;
  }

  public disable(): void {
    this.cancelTimeoutPlayingMedia();
    this.cancelTimeoutStart();

    this.emit("resting-updated", DEFAULT_RESPONSE);
  }

  public async startMediaDirect(
    resting: ProfileRestingModelEntry,
  ): Promise<void> {
    this.cancelTimeoutPlayingMedia();
    this.cancelTimeoutStart();
    await this.play(resting);
  }

  private cancelTimeoutStart(): void {
    if (this.timeoutStart) {
      clearTimeout(this.timeoutStart);
      this.timeoutStart = undefined;
      console.log(
        "[wall-engine-resting] Cancelled timeout start resting media",
      );
    }
  }

  private cancelTimeoutPlayingMedia(): void {
    if (this.timeoutPlayingMedia) {
      clearTimeout(this.timeoutPlayingMedia);
      this.timeoutPlayingMedia = undefined;
      console.log("[wall-engine-resting] Cancelled timeout playing media");
    }
  }

  private async getStaleTimeoutDelay(): Promise<number> {
    const profile = this.profile
      ? await getProfile(this.client, this.profile)
      : await getLocationActiveProfile(this.client, this.location!);

    if (
      typeof profile?.resting?.staleStateTimeout === "number" &&
      profile.resting.staleStateTimeout > 0
    ) {
      return profile.resting.staleStateTimeout * 1000;
    }

    return 0;
  }

  private async start(): Promise<void> {
    if (this.starting) {
      return;
    }
    this.starting = true;
    console.log("[wall-engine-resting] Starting...");

    this.cancelTimeoutPlayingMedia();

    const profile = this.profile
      ? await getProfile(this.client, this.profile)
      : await getLocationActiveProfile(this.client, this.location!);

    if (profile?.resting?.items?.length) {
      this.mediaIndex =
        this.mediaIndex >= profile.resting.items.length - 1
          ? 0
          : this.mediaIndex + 1;

      const resting = profile.resting.items[this.mediaIndex];
      if (resting) {
        await this.play(resting);
      }
    }

    console.log("[wall-engine-resting] Started");
    this.starting = false;
  }

  private async play(resting: ProfileRestingModelEntry): Promise<void> {
    if (resting.media) {
      const media = await getMedia(this.client, resting.media);
      if (media) {
        const mediaType = getMediaType(media);
        if (mediaType === "image") {
          const showTime =
            typeof resting.imageSettings?.showTime === "number"
              ? resting.imageSettings.showTime * 1000
              : DEFAULT_IMAGE_DURATION_MS;
          this.timeoutPlayingMedia = setTimeout(
            () => this.activate(),
            showTime,
          );
        }

        this.emit("resting-updated", {
          active: true,
          audio: !!resting.playAudio,
          imageSettings: resting.imageSettings ?? undefined,
          interrupt: !!resting.canInterrupt,
          media,
          transparent: !!resting.alphaOn,
          type: mediaType,
        });

        console.log(
          "[wall-engine-resting] Playing media:",
          resting.media,
          "Type:",
          mediaType,
        );
      }
    }
  }
}
